import React from "react";
import styled from "styled-components";

import Button from "../button/Button";
import type { SoremonAction } from "../../app/services/soremon";

export interface SoremonActionConfirmProps {
  action: SoremonAction;
  /** 実行 / キャンセルの送信中 */
  disabled?: boolean;
  onConfirm: (id: number) => void;
  onCancel: (id: number) => void;
}

const MAX_VALUE_CHARS = 200;

const formatValue = (value: unknown): string => {
  const text =
    typeof value === "string" ? value : JSON.stringify(value, null, 1) ?? String(value);
  return text.length > MAX_VALUE_CHARS ? `${text.slice(0, MAX_VALUE_CHARS)}…` : text;
};

const Card = styled.div`
  border: 1px solid var(--soremo-border);
  border-radius: 8px;
`;

const ArgumentValue = styled.span`
  word-break: break-all;
  white-space: pre-wrap;
`;

const STATUS_TEXT: Record<Exclude<SoremonAction["status"], "pending">, { text: string; className: string }> = {
  executed: { text: "実行しました", className: "u-text-blue" },
  cancelled: { text: "キャンセルしました", className: "u-text-light-gray" },
};

/**
 * soremon が実行しようとしている、確認が必要な操作（削除・送信・共有・契約など）の確認カード。
 * 確認待ちのときだけ「実行する / キャンセル」を出し、処理後は結果の文言に置き換える。
 */
const SoremonActionConfirm: React.FC<SoremonActionConfirmProps> = ({
  action,
  disabled = false,
  onConfirm,
  onCancel,
}) => {
  const entries = Object.entries(action.arguments ?? {});
  return (
    <Card className="u-col u-gap8 u-bg-white u-ptb8 u-pl12 u-pr16">
      <div className="heading-13 u-row u-items-center u-gap4">
        <span className="material-symbols-rounded u-text-blue">edit_note</span>
        {action.label}
      </div>
      {entries.length > 0 && (
        <div className="u-col u-gap2">
          {entries.map(([key, value]) => (
            <div key={key} className="bodytext-11 u-row u-gap8">
              <span className="u-text-light-gray u-flex-none">{key}</span>
              <ArgumentValue>{formatValue(value)}</ArgumentValue>
            </div>
          ))}
        </div>
      )}
      {action.status === "pending" ? (
        <div className="u-row u-gap8 u-justify-end">
          <Button
            size="small"
            theme="tertiary"
            width="fit"
            disabled={disabled}
            onClick={() => onCancel(action.id)}
          >
            キャンセル
          </Button>
          <Button
            size="small"
            theme="primary"
            width="fit"
            disabled={disabled}
            onClick={() => onConfirm(action.id)}
          >
            実行する
          </Button>
        </div>
      ) : (
        <div className={`bodytext-11 u-text-right ${STATUS_TEXT[action.status].className}`}>
          {STATUS_TEXT[action.status].text}
        </div>
      )}
    </Card>
  );
};

export default SoremonActionConfirm;
